<template>
  <div class="payment-page">
    <div class="payment-container">
      <div class="page-header">
        <button class="back-btn" @click="router.push('/account')">
          <span class="arrow">&larr;</span> 返回账号管理
        </button>
        <h2 class="page-title">算力中心</h2>
        <p class="page-sub">开通会员解锁充值，购买算力使用 AI 功能</p>
      </div>

      <!-- Subscription Section -->
      <div class="section">
        <div class="section-header">
          <h3 class="section-title">会员订阅</h3>
          <span class="section-desc">订阅即送算力，并解锁充值功能</span>
        </div>
        <div class="plans-grid">
          <div
            v-for="plan in subscriptionPlans"
            :key="plan.key"
            class="plan-card"
            :class="{ active: selectedPlan === plan.key, recommended: plan.key === 'SUBSCRIPTION_YEARLY' }"
            @click="selectedPlan = plan.key"
          >
            <div class="plan-badge" v-if="plan.key === 'SUBSCRIPTION_YEARLY'">推荐</div>
            <div class="plan-name">{{ plan.label }}</div>
            <div class="plan-price"><span class="currency">&yen;</span>{{ plan.price }}<span class="period">{{ plan.period }}</span></div>
            <div class="plan-credits">赠送 {{ plan.credits }} 算力</div>
            <div class="plan-check" v-if="selectedPlan === plan.key">&#10003;</div>
          </div>
        </div>
        <button class="pay-btn" @click="handlePay('SUBSCRIPTION')" :disabled="paying">
          <span v-if="paying" class="spinner"></span>
          {{ paying ? '正在生成二维码...' : '立即订阅' }}
        </button>
      </div>

      <!-- Recharge Section -->
      <div class="section">
        <div class="section-header">
          <h3 class="section-title">算力充值</h3>
          <span class="section-desc" v-if="subscribed">会员有效，充值功能已解锁</span>
          <span class="section-desc locked" v-else>请先开通会员订阅</span>
        </div>
        <div class="plans-grid recharge-grid" :class="{ locked: !subscribed }">
          <div class="lock-overlay" v-if="!subscribed">
            <div class="lock-icon">&#128274;</div>
            <p>开通会员后解锁充值</p>
          </div>
          <div
            v-for="plan in rechargePlans"
            :key="plan.totalFee"
            class="plan-card recharge-card"
            :class="{ active: selectedRecharge === plan.totalFee }"
            @click="subscribed && (selectedRecharge = plan.totalFee)"
          >
            <div class="plan-name">{{ plan.label }}</div>
            <div class="plan-price"><span class="currency">&yen;</span>{{ plan.price }}</div>
            <div class="plan-credits">{{ plan.credits }} 算力</div>
          </div>
        </div>
        <button
          class="pay-btn"
          @click="handlePay('RECHARGE')"
          :disabled="paying || !subscribed"
          v-if="subscribed"
        >
          <span v-if="paying" class="spinner"></span>
          {{ paying ? '正在生成二维码...' : '立即充值' }}
        </button>
      </div>

      <div class="tips">
        <p>支付即表示同意《算力充值服务协议》，充值后算力即时到账</p>
      </div>
    </div>

    <!-- QR Code Modal -->
    <div class="modal-overlay" v-if="showQrModal" @click.self="closeQrModal">
      <div class="qr-modal">
        <div class="qr-header">
          <h3>微信扫码支付</h3>
          <button class="close-btn" @click="closeQrModal">&times;</button>
        </div>

        <div class="qr-body">
          <div class="qr-amount">
            <span class="amount-label">支付金额</span>
            <span class="amount-value">&yen;{{ currentAmount }}</span>
          </div>

          <div class="qr-wrapper">
            <canvas ref="qrCanvas" width="220" height="220"></canvas>
            <div class="qr-overlay" v-if="paymentStatus === 'PAID'">
              <div class="paid-icon">&#10003;</div>
              <span>支付成功</span>
            </div>
          </div>

          <p class="qr-tip" v-if="paymentStatus === 'PENDING'">请使用微信扫描上方二维码完成支付</p>
          <p class="qr-tip success" v-else-if="paymentStatus === 'PAID'">支付成功！算力已充值到账</p>
        </div>

        <div class="qr-footer" v-if="paymentStatus === 'PAID'">
          <button class="btn-primary" @click="closeQrModal">完成</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/useAuthStore'
import { createOrder, queryOrderStatus, getSubscriptionStatus } from '@/api/payApi'
import { drawQRCode } from '@/utils/qrcode'

const router = useRouter()
const authStore = useAuthStore()

const subscriptionPlans = [
  { key: 'SUBSCRIPTION_MONTHLY', label: '月卡会员', price: '9.9', totalFee: 990, credits: 105, period: '/月' },
  { key: 'SUBSCRIPTION_YEARLY', label: '年卡会员', price: '59.9', totalFee: 5990, credits: 650, period: '/年' }
]

const rechargePlans = [
  { key: 'RECHARGE', label: '100算力', price: '10', totalFee: 1000, credits: 100 },
  { key: 'RECHARGE', label: '1010算力', price: '99', totalFee: 9900, credits: 1010 },
  { key: 'RECHARGE', label: '6999算力', price: '662', totalFee: 66200, credits: 6999 },
  { key: 'RECHARGE', label: '31600算力', price: '2870', totalFee: 287000, credits: 31600 }
]

const selectedPlan = ref('SUBSCRIPTION_YEARLY')
const selectedRecharge = ref(1000)
const paying = ref(false)
const showQrModal = ref(false)
const paymentStatus = ref('PENDING')
const currentAmount = ref('')
const qrCanvas = ref(null)
let pollTimer = null

const subInfo = ref(null)
const subscribed = computed(() => {
  if (!subInfo.value) return false
  return subInfo.value.status === 'ACTIVE'
})

onMounted(async () => {
  try {
    subInfo.value = await getSubscriptionStatus()
  } catch (e) {
    subInfo.value = null
  }
})

const handlePay = async (type) => {
  if (paying.value) return
  paying.value = true
  paymentStatus.value = 'PENDING'

  try {
    let orderType, totalFee, credits, amount

    if (type === 'SUBSCRIPTION') {
      const plan = subscriptionPlans.find(p => p.key === selectedPlan.value)
      orderType = plan.key
      totalFee = plan.totalFee
      credits = plan.credits
      amount = plan.price
    } else {
      const plan = rechargePlans.find(p => p.totalFee === selectedRecharge.value)
      orderType = 'RECHARGE'
      totalFee = plan.totalFee
      credits = plan.credits
      amount = plan.price
    }

    currentAmount.value = amount
    const res = await createOrder({ totalFee, credits, orderType })
    showQrModal.value = true

    await nextTick()
    drawQRCode(qrCanvas.value, res.codeUrl, 220)

    startPolling(res.outTradeNo)
  } catch (e) {
    alert(e.message || '创建订单失败')
  } finally {
    paying.value = false
  }
}

const startPolling = (outTradeNo) => {
  stopPolling()
  pollTimer = setInterval(async () => {
    try {
      const res = await queryOrderStatus(outTradeNo)
      if (res.status === 'PAID') {
        paymentStatus.value = 'PAID'
        stopPolling()
        await authStore.refreshProfileSilently()
        try { subInfo.value = await getSubscriptionStatus() } catch (e) { /* ignore */ }
      }
    } catch (e) {
      // keep polling
    }
  }, 3000)
}

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const closeQrModal = () => {
  showQrModal.value = false
  stopPolling()
}

onUnmounted(() => {
  stopPolling()
})
</script>

<style scoped>
.payment-page {
  padding: 2rem;
  background-color: #f8f9fb;
  min-height: calc(100vh - 64px);
  display: flex;
  justify-content: center;
}

.payment-container {
  width: 100%;
  max-width: 800px;
}

.page-header { margin-bottom: 2rem; }
.back-btn {
  background: none; border: none; color: #1677ff; font-size: 0.9rem;
  cursor: pointer; padding: 0; margin-bottom: 1rem;
}
.back-btn:hover { color: #4096ff; }
.arrow { margin-right: 4px; }
.page-title { font-size: 1.5rem; font-weight: 600; color: #1a1a1a; margin: 0 0 0.4rem 0; }
.page-sub { color: #666; font-size: 0.95rem; margin: 0; }

.section {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #ebeef5;
  position: relative;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.2rem;
}

.section-title { font-size: 1.1rem; font-weight: 600; color: #333; margin: 0; }
.section-desc { font-size: 0.85rem; color: #999; }
.section-desc.locked { color: #ff4d4f; }

.plans-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.2rem;
}

.recharge-grid {
  grid-template-columns: repeat(4, 1fr);
  position: relative;
}

.recharge-grid.locked { opacity: 0.5; pointer-events: none; }

.lock-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  background: rgba(255,255,255,0.7);
  border-radius: 12px;
}
.lock-overlay p { color: #666; font-size: 0.9rem; margin: 0.5rem 0 0; }
.lock-icon { font-size: 2rem; }

.plan-card {
  position: relative;
  background: #fafbfc;
  border: 2px solid #e8e8e8;
  border-radius: 12px;
  padding: 1.5rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}
.plan-card:hover { border-color: #b3d4ff; }
.plan-card.active { border-color: #1677ff; box-shadow: 0 0 0 1px #1677ff; }
.plan-card.recommended { border-color: #1677ff; }

.plan-badge {
  position: absolute;
  top: -1px; right: 16px;
  background: #1677ff; color: #fff;
  font-size: 0.75rem; padding: 2px 10px;
  border-radius: 0 0 6px 6px;
}

.plan-name { font-size: 1rem; font-weight: 600; color: #333; margin-bottom: 0.6rem; }

.plan-price {
  font-size: 1.8rem; font-weight: 700; color: #ff4d4f; margin-bottom: 0.4rem;
}
.recharge-card .plan-price { font-size: 1.4rem; }
.currency { font-size: 0.9rem; vertical-align: super; }
.period { font-size: 0.8rem; color: #999; font-weight: 400; }

.plan-credits { font-size: 0.9rem; color: #1677ff; font-weight: 500; }

.plan-check {
  position: absolute; top: 12px; left: 14px;
  width: 22px; height: 22px; background: #1677ff; color: #fff;
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 0.75rem;
}

.pay-btn {
  width: 100%; padding: 0.85rem;
  background: #1677ff; color: #fff; border: none; border-radius: 8px;
  font-size: 1rem; font-weight: 600; cursor: pointer; transition: background 0.2s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.pay-btn:hover { background: #4096ff; }
.pay-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.spinner {
  width: 16px; height: 16px;
  border: 2px solid #fff; border-top-color: transparent;
  border-radius: 50%; animation: spin 0.6s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.tips { text-align: center; color: #999; font-size: 0.85rem; }
.tips p { margin: 0; }

/* QR Modal */
.modal-overlay {
  position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
  background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center;
  z-index: 2500; backdrop-filter: blur(2px);
}

.qr-modal {
  background: #fff; border-radius: 16px; width: 360px; overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18); animation: modalFadeIn 0.2s ease-out;
}

.qr-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 1rem 1.5rem; border-bottom: 1px solid #f0f0f0;
}
.qr-header h3 { margin: 0; font-size: 1.1rem; color: #333; }
.close-btn { background: none; border: none; font-size: 1.5rem; color: #999; cursor: pointer; line-height: 1; }
.close-btn:hover { color: #333; }

.qr-body { padding: 1.5rem; display: flex; flex-direction: column; align-items: center; }

.qr-amount {
  display: flex; justify-content: space-between; width: 100%;
  margin-bottom: 1rem; padding: 0.6rem 1rem;
  background: #f6f8fa; border-radius: 8px;
}
.amount-label { color: #666; font-size: 0.9rem; }
.amount-value { color: #ff4d4f; font-weight: 700; font-size: 1.1rem; }

.qr-wrapper { position: relative; margin-bottom: 1rem; }
.qr-wrapper canvas { border-radius: 8px; border: 1px solid #eee; }

.qr-overlay {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(255,255,255,0.92); border-radius: 8px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; color: #52c41a; font-weight: 600;
}
.paid-icon {
  width: 40px; height: 40px; background: #52c41a; color: #fff;
  border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 1.3rem;
}

.qr-tip { font-size: 0.9rem; color: #666; margin: 0; }
.qr-tip.success { color: #52c41a; font-weight: 500; }

.qr-footer { padding: 0 1.5rem 1.5rem; display: flex; justify-content: center; }
.btn-primary {
  width: 100%; padding: 0.65rem; background: #1677ff; color: #fff;
  border: none; border-radius: 8px; font-size: 0.95rem; cursor: pointer;
}
.btn-primary:hover { background: #4096ff; }

@keyframes modalFadeIn {
  from { opacity: 0; transform: translateY(-20px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@media (max-width: 768px) {
  .plans-grid { grid-template-columns: 1fr; }
  .recharge-grid { grid-template-columns: repeat(2, 1fr); }
  .qr-modal { width: 92%; }
}
</style>
